import type { Person } from '@shared/modules/people/person';

export const TYPE_CARER_TEAMS_MISMATCH = 'carer_teams_mismatch';
export const TYPE_CARER_UNAVAILABILITY = 'carer_unavailability';
export const TYPE_CLIENT_TEAMS_MISMATCH = 'client_teams_mismatch';
export const TYPE_CLIENT_UNAVAILABILITY = 'client_unavailability';
export const TYPE_MIN_CARERS = 'min_carers_not_met';
export const TYPE_MISSING_BOOKING_TYPE = 'missing_booking_type';
export const TYPE_MISSING_SERVICE_CONTRACT = 'missing_service_contract';
export const TYPE_MISSING_TEAMS = 'teams_missing';
export const TYPE_SERVICE_CONTRACT_FINISHED = 'service_contract_finished';
export const TYPE_SERVICE_CONTRACT_INACTIVE = 'service_contract_inactive';
export const TYPE_SHIFT_CARER_CONTRACT_FINISHED = 'shift_carer_contract_finished';
export const TYPE_SHIFT_CARER_CONTRACT_INACTIVE = 'shift_carer_contract_inactive';
export const TYPE_SHIFT_MISSING_CARER_CONTRACT = 'shift_missing_team';
export const TYPE_SHIFT_MISSING_TEAM = 'shift_missing_team';
export const TYPE_UNALLOCATED = 'unallocated';
export const TYPE_UNAVAILABILITY = 'unavailability';

export interface Warning {
    [prop: string]: any;

    type: string;
    message?: string;
    label?: string;
    person_id?: string;
    person_name?: string;

    person?: { data: Person };
}

export function warningLabel({ type }) {
    switch (type) {
        case TYPE_MIN_CARERS:
        case TYPE_UNALLOCATED:
            return 'Carers';

        case TYPE_MISSING_BOOKING_TYPE:
            return 'Booking Type';

        case TYPE_MISSING_SERVICE_CONTRACT:
        case TYPE_SERVICE_CONTRACT_FINISHED:
        case TYPE_SERVICE_CONTRACT_INACTIVE:
            return 'Service Contract';

        case TYPE_UNAVAILABILITY:
            return 'Unavailable';

        case TYPE_CARER_UNAVAILABILITY:
            return 'Carer Unavailable';

        case TYPE_CLIENT_UNAVAILABILITY:
            return 'Client Unavailable';

        case TYPE_CLIENT_TEAMS_MISMATCH:
        case TYPE_CARER_TEAMS_MISMATCH:
            return 'Team Mismatch';

        case TYPE_MISSING_TEAMS:
            return 'Teams';
    }

    return type;
}

/**
 * @param warning
 * @return
 */
export function warningMessage(warning) {
    const { type, message } = warning;

    if (type === TYPE_CLIENT_TEAMS_MISMATCH) {
        const { teams } = warning;

        return `Client does not belongs to teams: ${teams.map(t => t.description)}`;
    }

    if (type === TYPE_CARER_TEAMS_MISMATCH) {
        const { teams } = warning;

        return `Carer does not belongs to teams: ${teams.map(t => t.description)}`;
    }

    switch (type) {
        case TYPE_MISSING_BOOKING_TYPE:
        case TYPE_MISSING_TEAMS:
        case TYPE_MISSING_SERVICE_CONTRACT:
            return 'Required';

        case TYPE_SERVICE_CONTRACT_INACTIVE:
            return 'Inactive';

        case TYPE_SERVICE_CONTRACT_FINISHED:
            return 'Finished';
    }

    return message;
}

/**
 * @param warnings
 * @return
 */
export function formatWarnings(warnings: Warning[]): Warning[] {
    return warnings.map(warning => ({
        type: warning.type,
        label: warningLabel(warning),
        message: warningMessage(warning),
    }));
}

/**
 * @param allowedTeams
 * @param actualTeams
 * @param subject
 * @return
 */
export function createWarningForTeams(allowedTeams, actualTeams, subject = 'client'): Warning[] {
    const warnings = [];

    if (actualTeams.length === 0) {
        const type = TYPE_MISSING_TEAMS;

        warnings.push({
            type,
            label: warningLabel({ type }),
            message: warningMessage({ type }),
        });
    } else {
        const notAllowed = actualTeams.filter(team => !allowedTeams.some(t => t.id === team.id));

        if (notAllowed.length) {
            const type = subject === 'carer' ? TYPE_CARER_TEAMS_MISMATCH : TYPE_CLIENT_TEAMS_MISMATCH;

            warnings.push({
                type,
                label: warningLabel({ type }),
                message: warningMessage({ type, teams: notAllowed }),
            });
        }
    }

    return warnings;
}

export function getWarningIcon(warning: Warning): string {
    switch (warning.type) {
        case 'confirmation_location_arrival':
        case 'confirmation_location_departure':
            return 'location_off';

        case 'confirmation_duration_over':
        case 'confirmation_duration_under':
            return 'timer';

        case 'confirmation_punctuality':
        case 'confirmation_scan_delay':
            return 'schedule';

        case 'manual_check_in':
            return 'login';

        case 'manual_check_out':
            return 'logout';
    }

    return 'warning';
}
